import React, { useEffect, useState } from 'react';
import FormControl from '../../../../../Components/Form/FormControl';
import Button from '../../../../../Components/Button/Button';
import Text from '../../../../../Components/Base/Text/Text';
import FormContainer from '../../../../../Components/Form/FormContainer';
import DatePicker from '../../../../../Components/Base/DatePicker/DatePicker';
import SubmitButton from '../../../../../Components/Button/SubmitButton';
import PhoneNumberField from '../../../../../Components/PhoneNumber/PhoneNumberField';
import TermItemSelectField from '../../../../Term/Components/TermItemSelectField';
import { Moment } from 'moment';
import FormWrapper from '../../../../../Components/Form/FormWrapper';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import CitizenshipSelectField from '../../../../Base/Components/Fields/Citizenship/CitizenshipSelectField';
import InputField from '../../../../../Components/Form/components/InputField';
import CheckboxField from '../../../../../Components/Form/components/CheckboxField';
import { SelectValue } from '../../../../../Components/Base/Select/Select';
import { Theme } from '../../../../../Enums/Theme.enum';
import useNotification from '../../../../../Hooks/useNotification.hook';
import Loading from '../../../../../Components/Loading';
import {
	TermItem,
	useLazyTermItemByValueQuery,
} from '../../../../../graphql/graphql';
import { useEffectAsync } from '../../../../../helpers';
import usePatient from '../../../../Patient/Hooks/usePatient';
import PatientRegistrationHealthPlanSelect from '../Utils/PatientRegistrationHealthPlanSelect';
import useNCZI from '../../../../Nczi/Hooks/useNCZI';
import { useNCZIContext } from '../../../../Nczi/Services/NCZIContext';
import NCZICommandResult from '../../../../Nczi/Components/NCZICommandResult/NCZICommandResult';
import { NCZIReferenceSelectItem } from '../../../../Nczi/Components/ReferenceSearchSelect/NCZIReferenceSearchSelect';
import { NCZI_OID } from '../../../../Nczi/Enums/ncziOid.enum';
import NCZIReferenceSelectField from '../../../../Nczi/Components/ReferenceSelect/NCZIReferenceSelectField';
import usePatientManualRegistrationSlovakiaFormValidationScheme from '../../../Validation/PatientManualRegistrationSlovakiaFormValidationScheme';
import useNCZIDefaultValues from '../../../../Nczi/Hooks/useNCZIDefaultValues';
import GenderSelectField from '../../../../Base/Components/Inputs/GenderSelect/GenderSelectField';
import usePatientRegistration from '../../../Hooks/usePatientRegistration';

// @ts-ignore
import styles from './../PatientManualRegistration.module.scss';
import PatientRegistrationFavoritePresetSelect from '../Utils/PatientRegistrationFavoritePresetSelect';

export interface PatientManualRegistrationSlovakiaFormValues {
	insuranceNumber: string;
	salutationBefore?: string;
	salutationAfter?: string;
	firstName: string;
	lastName: string;
	email: string;
	address: {
		street: string;
		descriptiveNumber: string;
		guideNumber?: string;
		city: string;
		postCode: string;
	};
	healthInsuranceCompany?: SelectValue;
	citizenship: SelectValue;
	birthDate?: Moment;
	gender?: SelectValue;
	phoneNumber?: string;
	sendPatientInviteToMeddi: boolean;
	healthPlanCode?: SelectValue;
	healthPlanWorkplaceMember?: {
		value?: string;
		id?: string;
		label: string;
		item: any;
	};
	legalRepresentative?: {
		firstName: string;
		lastName: string;
		email: string;
		phoneNumber?: string;
	};
	state: NCZIReferenceSelectItem;
	createdByType: SelectValue;
	createdById?: {
		id?: string;
		label: string;
		item: {
			user: {
				id: string;
			};
		};
	};
}

interface Props {
	onSuccess: (
		values: PatientManualRegistrationSlovakiaFormValues,
		withHealthPlan: boolean,
	) => void;
}

const PatientManualRegistrationSlovakiaForm: React.FC<Props> = ({
	onSuccess,
}) => {
	const { showNotificationAlert } = useNotification();
	const { referenceDefaultValue } = useNCZIDefaultValues();
	const [isLoading, setIsLoading] = useState(false);
	const [
		isAllowToCheckWithNcziAndInsurance,
		setIsAllowToCheckWithNcziAndInsurance,
	] = useState(false);
	const [isChild, setIsChild] = useState(false);
	const [citizenshipOptions, setCitizenshipOptions] = useState<
		TermItem[] | undefined
	>();
	const { checkIsAdultByInsuranceNumber, checkIsAdultByBirthDate } =
		usePatient();
	const [isGenderRequested, setIsGenderRequested] = useState(false);
	const { validationSchema } =
		usePatientManualRegistrationSlovakiaFormValidationScheme(
			isChild,
			isGenderRequested,
		);
	const { fetchPatientSummaryContactData, fetchPatientIdentifier } = useNCZI();
	const [getTermItemByValue] = useLazyTermItemByValueQuery();
	const { patientSummaryContactData, patientIdentifier } = useNCZIContext();
	const [loadingCommandId, setLoadingCommandId] = useState<string | undefined>(
		undefined,
	);
	const defaultValues: any = {
		sendPatientInviteToMeddi: true,
	};
	const [isEmailFilled, setIsEmailFilled] = useState(false);

	const formMethods = useForm({
		defaultValues: defaultValues,
		resolver: yupResolver(validationSchema),
	});

	const {
		isGenderRequested: isGenderRequestedFromHook,
		isBirthDateRequested,
		insuranceNumberValue,
		healthPlanCodeValue,
	} = usePatientRegistration({
		formMethods,
		setIsChild,
		citizenshipOptions,
	});

	useEffect(() => {
		setIsGenderRequested(isGenderRequestedFromHook);
	}, [isGenderRequestedFromHook]);

	const emailValue = formMethods.watch('email');

	useEffect(() => {
		setIsEmailFilled((emailValue?.length || 0) > 0);
	}, [emailValue]);

	const handleLoadData = async () => {
		if (!patientIdentifier) {
			if (insuranceNumberValue) {
				const command = await fetchPatientIdentifier(
					insuranceNumberValue.replace('/', '').trim(),
				);
				if (command.id) {
					setLoadingCommandId(command.id);
				}
			}
		}
	};

	useEffectAsync(async () => {
		if (patientIdentifier) {
			const command = await fetchPatientSummaryContactData(patientIdentifier);
			if (command.id) {
				setLoadingCommandId(command.id);
			}
		}
	}, [patientIdentifier]);

	const handleLoadDataFromNCZI = async () => {
		setIsLoading(true);
		try {
			if (insuranceNumberValue) {
				handleLoadData();
			}
		} catch (e: any) {
			if (e.response.data.displayableError) {
				showNotificationAlert({
					content: e.response.data.message,
				});
			}
		}
	};

	const handleSuccess = (values: any) => {
		onSuccess(values as PatientManualRegistrationSlovakiaFormValues, false);
	};

	const handleSuccessWithPlan = (values: any) => {
		onSuccess(values as PatientManualRegistrationSlovakiaFormValues, true);
	};

	useEffect(() => {
		const cleanInsuranceNumber = insuranceNumberValue?.replace('/', '')?.trim();
		setIsAllowToCheckWithNcziAndInsurance(cleanInsuranceNumber?.length >= 9);
		if (cleanInsuranceNumber && cleanInsuranceNumber?.length >= 6) {
			setIsChild(!checkIsAdultByInsuranceNumber(cleanInsuranceNumber));
		}
		if (cleanInsuranceNumber?.length < 6) {
			setIsChild(false);
		}
	}, [insuranceNumberValue]);

	useEffectAsync(async () => {
		if (patientSummaryContactData) {
			const jruz = patientSummaryContactData.patientJRUZ;
			const patientData = {
				firstName: jruz?.person?.firstName ?? undefined,
				lastName: jruz?.person?.lastName ?? undefined,
				address: {
					street: jruz?.address?.street ?? undefined,
					descriptiveNumber: jruz?.address?.descriptiveNumber ?? undefined,
					guideNumber: jruz?.address?.guideNumber ?? undefined,
					city: jruz?.address?.city?.displayName ?? undefined,
					postCode: jruz?.address?.postCode?.displayName ?? undefined,
				},
				email: patientSummaryContactData.patientContact?.email ?? undefined,
				phoneNumber:
					patientSummaryContactData.patientContact?.phone ?? undefined,
				state: jruz?.address?.country
					? referenceDefaultValue(jruz?.address?.country)
					: undefined,
			};

			if (jruz?.insurance?.company?.code?.value) {
				const { termItem: insuranceTermItem } = await getTermItemByValue({
					termType: 'insurance_companies',
					value: jruz?.insurance?.company?.code?.value,
				}).unwrap();

				if (insuranceTermItem) {
					// @ts-ignore
					patientData['healthInsuranceCompany'] = {
						label: insuranceTermItem.name,
						value: insuranceTermItem.id,
					};
				}
			}

			if (jruz?.person?.sex?.code) {
				const { termItem: genderTermItem } = await getTermItemByValue({
					termType: 'gender',
					value: jruz.person.sex.code === '00000116160' ? 'female' : 'male',
				}).unwrap();

				if (genderTermItem) {
					// @ts-ignore
					patientData['gender'] = {
						label: genderTermItem.name,
						value: genderTermItem.id,
					};
				}
			}

			formMethods.reset(patientData);
			setIsLoading(false);
		}
	}, [patientSummaryContactData]);

	if ((!patientSummaryContactData || !patientIdentifier) && isLoading) {
		return (
			<div className={styles.wrapper}>
				<div className={styles.content}>
					<NCZICommandResult
						commandId={loadingCommandId}
						handleRetryCommand={handleLoadData}
					/>
				</div>
			</div>
		);
	}

	return (
		<FormWrapper formMethods={formMethods}>
			<div className='v2-forms__gray-background'>
				<div className='row'>
					<div className='col-lg-4 col-12'>
						<InputField
							name='insuranceNumber'
							label='base.shared.insuranceNumber'
							isRequired
						/>
					</div>
					<div className='col-lg-8 col-12'>
						<Text
							tx='base.verificationOfNamePermanentResidenceAddressAndInsuranceValidity'
							className={styles.hintText}
						/>
						{isLoading ? (
							<Loading />
						) : (
							<Button
								title='nczi.loadData'
								onClick={handleLoadDataFromNCZI}
								disabled={!isAllowToCheckWithNcziAndInsurance}
								theme={
									isAllowToCheckWithNcziAndInsurance
										? Theme.BLUE
										: Theme.DISABLED
								}
							/>
						)}
					</div>
				</div>
			</div>
			{!isChild && (
				<div className='row mb-3'>
					<div className='col-lg-6 col-12'>
						<InputField
							name='salutationBefore'
							label='base.shared.salutationBefore'
							charactersLimit={50}
							showCharCounter={false}
						/>
					</div>
					<div className='col-lg-6 col-12'>
						<InputField
							name='salutationAfter'
							label='base.shared.salutationAfter'
							charactersLimit={50}
							showCharCounter={false}
						/>
					</div>
				</div>
			)}
			<div className='row'>
				<div className='col-lg-6 col-12'>
					<InputField
						name='firstName'
						label='base.shared.firstName'
						isRequired
					/>
				</div>
				<div className='col-lg-6 col-12'>
					<InputField name='lastName' label='base.shared.lastName' isRequired />
				</div>
			</div>
			<Text className={styles.formText} tx='base.permanentAddress' firstUpper />
			<FormContainer name={'address'}>
				<div className='row mb-3'>
					<div className='col-lg-6 col-12'>
						<InputField name='street' label='base.shared.street' isRequired />
					</div>
					<div className='col-lg-3 col-12'>
						<InputField
							name='descriptiveNumber'
							label='base.shared.descriptiveNumber'
							isRequired
						/>
					</div>
					<div className='col-lg-3 col-12'>
						<InputField name='guideNumber' label='base.shared.guideNumber' />
					</div>
				</div>
				<div className='row'>
					<div className='col-lg-6 col-12'>
						<InputField name='city' label='base.shared.city' isRequired />
					</div>
					<div className='col-lg-3 col-12'>
						<InputField
							name='postCode'
							label='base.shared.postCode'
							isRequired
						/>
					</div>
				</div>
			</FormContainer>
			<Text
				className={styles.formText}
				tx='base.additionalInformations'
				firstUpper
			/>
			<FormControl
				name='healthInsuranceCompany'
				label='base.healthInsuranceCompany'>
				<TermItemSelectField
					name='healthInsuranceCompany'
					termType='insurance_companies'
					placeholder={'base.startTyping'}
				/>
			</FormControl>
			<div className='row mb-3'>
				<div className='col-lg-6 col-12'>
					<CitizenshipSelectField
						name='citizenship'
						label='base.citizenship'
						isRequired
						onLoad={setCitizenshipOptions}
					/>
				</div>
				<div className='col-lg-6 col-12'>
					<FormControl name='state' isRequired>
						<NCZIReferenceSelectField
							isRequired
							name='state'
							label='registration.address.country'
							oid={NCZI_OID.COUNTRY}
						/>
					</FormControl>
				</div>
				{isBirthDateRequested && (
					<div className='col-lg-6 col-12'>
						<FormControl
							name='birthDate'
							label='base.shared.dateOfBirth'
							isRequired>
							<DatePicker />
						</FormControl>
					</div>
				)}
				{isGenderRequested && (
					<div className='col-lg-6 col-12'>
						<GenderSelectField
							valueKey='id'
							name='gender'
							label='base.shared.gender'
							isRequired
						/>
					</div>
				)}
			</div>
			{!isChild && (
				<div className='row mb-3'>
					<div className='col-lg-6 col-12'>
						<PhoneNumberField name='phoneNumber' label='base.shared.phone' />
					</div>
					<div className='col-lg-6 col-12'>
						<InputField name='email' label='base.shared.email' />
					</div>
				</div>
			)}
			<div className='mb-3'>
				<PatientRegistrationHealthPlanSelect formMethods={formMethods} />
			</div>
			<div className='mb-3'>
				<PatientRegistrationFavoritePresetSelect />
			</div>
			{isChild && (
				<>
					<Text
						className={styles.formText}
						tx='base.shared.legalRepresentative'
						firstUpper
					/>
					<FormContainer name={'legalRepresentative'}>
						<div className='row mb-3'>
							<div className='col-lg-6 col-12'>
								<InputField
									name='firstName'
									label='base.shared.firstName'
									isRequired
								/>
							</div>
							<div className='col-lg-6 col-12'>
								<InputField
									name='lastName'
									label='base.shared.lastName'
									isRequired
								/>
							</div>
						</div>
						<div className='row'>
							<div className='col-lg-6 col-12'>
								<PhoneNumberField
									name='phoneNumber'
									label='base.shared.phone'
								/>
							</div>
							<div className='col-lg-6 col-12'>
								<InputField name='email' label='base.shared.email' />
							</div>
						</div>
					</FormContainer>
				</>
			)}
			<div className='v2-forms__gray-background footer mt-3'>
				<div
					className='d-flex flex-row align-items-center'
					style={{ gap: '10px' }}>
					<div>
						<SubmitButton
							onSubmit={handleSuccess}
							title='base.registerThePatient'
						/>
					</div>
					{healthPlanCodeValue && healthPlanCodeValue !== 'withoutPlan' && (
						<div>
							<SubmitButton
								onSubmit={handleSuccessWithPlan}
								title='component.patientManualRegistrationComponent.registerAndSetupHealthPlan'
							/>
						</div>
					)}
					<div className='pl-3' style={{ width: '350px' }}>
						{isEmailFilled && (
							<>
								<Text
									tx='base.emailWithInvitationWillArriveToPatientsEmail'
									className='v2-forms__light-text'
									firstUpper
								/>
								<CheckboxField
									name='sendPatientInviteToMeddi'
									tx='base.sendPatientInviteToMeddi'
									isRequired
									className='mt-2'
								/>
							</>
						)}
					</div>
				</div>
			</div>
		</FormWrapper>
	);
};

export default PatientManualRegistrationSlovakiaForm;
